/** @jsxImportSource @emotion/react */
import { Typography, Box, Divider, Chip, Theme, useTheme } from '@mui/material';
import CheckPaneItem from 'components/CheckPaneItem';
import SwitcherOption from 'components/SwitcherOption';
import React, { ChangeEvent, Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { UseStyles } from 'types/styles';
import jsonData from './suggestions.json';
import { IPermissionOption, IPermissionSuggestion, PlanPermission } from 'types/dto';
import { ReactMarkdown } from 'react-markdown/lib/react-markdown';
import PlanUserList from './PlanUserList';

interface IPermissionHelperState {
  options: IPermissionOption[];
  permissions: IPermissionSuggestion[];
}
const defaultState = jsonData as IPermissionHelperState;

export const PermissionsHelper: React.FC<{
  countryId?: number;
  onChange?: (permissionId: PlanPermission) => void;
}> = ({ countryId, onChange }) => {
  const theme = useTheme() as Theme;
  const classes = useStyles(theme);
  const { formatMessage } = useIntl();
  const [state, setState] = useState<IPermissionHelperState>(defaultState);
  const [dirtyPermissions, setDirtyPermissions] = useState<Set<PlanPermission>>(new Set());
  const [isTouched, setIsTouched] = useState<boolean>(false);
  const suggestedPermisions = useMemo(() => {
    return new Set(
      state.options.reduce((suggestions, option) => {
        if (option.enable !== null) {
          if (option.enable) {
            suggestions.push(...(Object.keys(option.permissions.enable) as PlanPermission[]));
          } else {
            suggestions.push(...(Object.keys(option.permissions.disable) as PlanPermission[]));
          }
        }
        return suggestions;
      }, [] as PlanPermission[]),
    );
  }, [state.options]);
  const [checkedPermissions, setCheckedPermissions] = useState<Set<PlanPermission>>(new Set(suggestedPermisions));

  const createToggleOptionHandler = (optionId: number) => (enable: boolean) => {
    const stateUpdate = structuredClone(state);
    let isOptionExists = false;

    for (let o of stateUpdate.options) {
      const prevEnableValue = o.enable;

      if (!isOptionExists && o.id === optionId) {
        isOptionExists = true;
        if (prevEnableValue !== enable) {
          o.enable = enable;
        } else {
          o.enable = null;
        }
      }
    }

    setState(stateUpdate);
  };

  const createHandleChange = (permissionId: PlanPermission) => (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      checkPermission(permissionId);
    } else {
      uncheckPermission(permissionId);
    }
    markPermissionDirty(permissionId);
    setIsTouched(true);
  };

  const checkPermission = useCallback((permissionId: PlanPermission) => {
    const update = new Set([permissionId]);
    setCheckedPermissions(update);
  }, []);

  const uncheckPermission = useCallback(
    (permissionId: PlanPermission) => {
      const update = new Set(checkedPermissions);
      update.delete(permissionId);
      setCheckedPermissions(update);
    },
    [checkedPermissions],
  );

  const markPermissionDirty = useCallback(
    (permissionId: PlanPermission) => {
      const update = new Set(dirtyPermissions);
      update.add(permissionId);
      setDirtyPermissions(update);
    },
    [dirtyPermissions],
  );

  const getHighestPriorityPermissionId = () => {
    return Array.from(suggestedPermisions).shift();
  };

  const isSuggesstionLabelEnabled = (permissionId: PlanPermission): boolean => {
    // check is permission highest priority?
    return permissionId === getHighestPriorityPermissionId();
  };

  useEffect(() => {
    if (checkedPermissions) {
      onChange?.(Array.from(checkedPermissions)[0]);
    }
    // eslint-disable-next-line
  }, [onChange, Array.from(checkedPermissions).join()]);

  useEffect(() => {
    if (isTouched) return;

    const checked = new Set(checkedPermissions);

    state.permissions.forEach(permission => {
      if (!dirtyPermissions.has(permission.id)) {
        const highestPermissionId = getHighestPriorityPermissionId();
        checked.delete(permission.id);
        if (highestPermissionId) {
          checked.add(highestPermissionId);
        }
      }
    });

    setCheckedPermissions(checked);
    // eslint-disable-next-line
  }, [state.permissions, suggestedPermisions]);

  return (
    <>
      <Typography component="h2" variant="subtitle2" p={2} mt={2}>
        <FormattedMessage id={'UploadForm.dataInformation'} defaultMessage={'Data information'} />
      </Typography>
      <Box p={2} mb={4} css={[classes.form, classes.dataInfo]}>
        {state.options.map(option => (
          <SwitcherOption
            key={option.translationId}
            enable={option.enable}
            onToggle={createToggleOptionHandler(option.id)}
          >
            <FormattedMessage id={option.translationId} defaultMessage={option.title} />
          </SwitcherOption>
        ))}
      </Box>
      <Divider />
      <Typography component="h2" variant="subtitle2" p={2} mt={2}>
        <FormattedMessage id={'UploadForm.permissionSetting'} defaultMessage={'Permission setting'} />
      </Typography>
      <Box mt={4} mb={4} display="flex" flexDirection="column" alignItems="stretch" gap={4}>
        {state.permissions.map(permission => (
          <Fragment key={permission.id}>
            <CheckPaneItem
              checkboxProps={{
                checked: checkedPermissions?.has(permission.id),
                onChange: createHandleChange(permission.id),
              }}
            >
              <ReactMarkdown
                children={formatMessage({ id: permission.translationId, defaultMessage: permission.title })}
              />
              {isSuggesstionLabelEnabled(permission.id) && (
                <Chip
                  label={<FormattedMessage id={'UploadForm.recommended'} defaultMessage={'Recommended'} />}
                  css={classes.badge}
                />
              )}
            </CheckPaneItem>
            {permission.hasMembers && countryId && <PlanUserList countryId={countryId} />}
          </Fragment>
        ))}
      </Box>
    </>
  );
};

const useStyles = (theme: Theme): UseStyles => ({
  form: {
    display: 'inline-grid',
    gridTemplateColumns: 'minmax(200px, auto) 1fr',
    alignItems: 'flex-start',
    gap: 20,
    width: '100%',
  },
  dataInfo: {
    alignItems: 'center',
  },
  badge: {
    color: theme.palette.common.white,
    background: theme.palette.custom.secondary.green[100],
    marginInlineStart: 'auto',
  },
});
